import { PlusOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { Button, Form, Input, Tooltip, Typography } from 'antd';
import cn from 'classnames';

import type { ModelClientFieldValue } from '@app/api/Client';
import { useClientAdditionalFields } from '@app/components/EditClient/components/ClientAdditionalFields/hooks';
import { InlineFieldCreator } from '@app/components/EditClient/components/ClientAdditionalFields/ui/InlineFieldCreator';

import type { FC } from 'react';

const PF = 'client-data';
const FIELD_VALUE_MAX_LENGTH = 5000;
const { Text } = Typography;
const { Item } = Form;
const { TextArea } = Input;

interface ClientAdditionalFieldsProps {
  clientId: number | null;
  fieldDefs: ModelClientFieldValue[];
  isLoading: boolean;
  onFieldsChanged: () => Promise<void> | void;
  /**
   * important — группа важных полей сразу под «Основной телефон»: жирный
   * лейбл, без кнопки создания. regular — обычные поля внизу формы вместе с
   * кнопкой «Добавить поле».
   */
  variant: 'important' | 'regular';
}

/**
 * Блок дополнительных полей клиента: значения живут в основной форме
 * страницы под ключами ['ClientFieldValues', FieldId] и сохраняются общей
 * кнопкой «Сохранить». Рендерится строго внутри <Form> страницы (двумя
 * группами — важные и обычные), поэтому значения при смене важности не
 * теряются: имя поля в форме не меняется.
 */
export const ClientAdditionalFields: FC<ClientAdditionalFieldsProps> = ({
  clientId,
  fieldDefs,
  isLoading,
  onFieldsChanged,
  variant,
}) => {
  const {
    isInlineCreateOpen,
    isCreating,
    isTogglingImportance,
    openInlineCreate,
    closeInlineCreate,
    submitInlineCreate,
    toggleFieldImportance,
  } = useClientAdditionalFields({ clientId, onFieldsChanged });

  const isImportantGroup = variant === 'important';

  return (
    <>
      {fieldDefs.map((field) => {
        const { FieldId, FieldName } = field;

        return (
          <div
            key={FieldId}
            className={cn(`${PF}_fields_item`, `${PF}_fields_item--textarea`)}
          >
            <span className={`${PF}_fields_label-holder`}>
              <Text
                className={cn(`${PF}_fields_label`, {
                  [`${PF}_fields_label--important`]: isImportantGroup,
                })}
                data-testid={`client-additional-field-label-${FieldId}`}
              >
                {FieldName}
              </Text>
              <Tooltip
                title={
                  isImportantGroup ? 'Снять важность' : 'Сделать поле важным'
                }
              >
                <Button
                  type="text"
                  size="small"
                  className={cn(`${PF}_fields_importance-toggle`, {
                    [`${PF}_fields_importance-toggle--active`]:
                      isImportantGroup,
                  })}
                  icon={isImportantGroup ? <StarFilled /> : <StarOutlined />}
                  onClick={() => toggleFieldImportance(field)}
                  disabled={isLoading || isTogglingImportance}
                  data-testid={`client-additional-field-importance-${FieldId}`}
                  aria-label={
                    isImportantGroup ? 'Снять важность' : 'Сделать поле важным'
                  }
                />
              </Tooltip>
            </span>
            {/*
              Без `noStyle`: Form.Item сам держит стандартный отступ страницы,
              иначе дополнительные поля слипаются друг с другом. Вёрстка блока
              уже рассчитана на полноценный Form.Item — см. правило
              `&_item .ant-form-item { width: 100% }` в ClientData.desktop.scss.
            */}
            <Item
              name={['ClientFieldValues', String(FieldId)]}
              className={`${PF}_fields_textarea`}
            >
              <TextArea
                rows={2}
                maxLength={FIELD_VALUE_MAX_LENGTH}
                disabled={isLoading}
                data-testid={`client-additional-field-input-${FieldId}`}
              />
            </Item>
          </div>
        );
      })}
      {/*
        Кнопка стоит в колонке контролов (отступ слева = ширине колонки
        лейблов), как и остальные поля формы — см. `_fields_add-field` в
        ClientData.desktop.scss.
      */}
      {!isImportantGroup &&
        (isInlineCreateOpen ? (
          <InlineFieldCreator
            isSubmitting={isCreating}
            onSubmit={submitInlineCreate}
            onCancel={closeInlineCreate}
          />
        ) : (
          <div className={`${PF}_fields_add-field`}>
            <Button
              type="dashed"
              size="large"
              icon={<PlusOutlined />}
              onClick={openInlineCreate}
              disabled={isLoading}
              data-testid="client-additional-field-add"
            >
              Добавить поле
            </Button>
          </div>
        ))}
    </>
  );
};
